import React, { useState, useEffect, useContext, useRef } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import IconTextField from "../components/IconTextField";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import loginbg from "../assets/login-image.png";
import { useNavigate } from "react-router-dom";
import appTheme from "../themes/theme";
import { IconButton, LinearProgress, List, ListItem, ListItemText, Stack } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { completeNewPasswordChallenge } from "../services/jbwAuthServices";
import { AuthContext } from "../AuthContext";

// Reached only from the sign-in page, when Cognito answers a login with the
// NEW_PASSWORD_REQUIRED challenge (a temporary password from an admin-created account). The
// user has already proved themselves with that temporary password; jbwAuthServices holds the
// CognitoUser carrying the challenge, and completing it is the whole of the change.
//
// It used to take the target account from an encrypted :id in the URL, using a secret that
// ships inside this bundle -- so anyone could forge a link for any address, admins included.
// There is no longer anything in the URL to forge: no pending challenge, no page.
const ChangePassword = () => {
  const { requiredNewPwd, resetNewPasswordAttr, getCurrentUser } =
    useContext(AuthContext);
  let navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErr, setPasswordErr] = useState("");
  const [confirmPasswordErr, setConfirmPasswordErr] = useState("");
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [pwdChangeErr, setPwdChangeErr] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  // No challenge pending (a bookmark, a reload, or someone typing the URL) means there is no
  // account this form could belong to. Send them back to sign in rather than showing a form
  // that can only fail.
  // Set once the challenge succeeds, so this redirect cannot race the success navigate below
  // (which carries loginTriggered) and strand the user on the sign-in form.
  const completedRef = useRef(false);
  useEffect(() => {
    if (!requiredNewPwd && !completedRef.current) {
      navigate("/", { replace: true });
    }
  }, [requiredNewPwd, navigate]);

  const homeNavigation = () => {
    restForm();
    // Leaving drops the pending challenge, so SignIn does not bounce straight back here.
    resetNewPasswordAttr();
    navigate("/", { replace: true });
  };

  const formInputChange = (formField, value) => {
    if (formField === "newPassword") {
      setPassword(value);
    }
    if (formField === "confirmPassword") {
      setConfirmPassword(value);
    }
  };

  const validation = () => {
    return new Promise((resolve, reject) => {
      if (password === "" && confirmPassword === "") {
        setPasswordErr("New Password required");
        setConfirmPasswordErr("Confirm Password required");
        resolve({
          password: "New Password required",
          confirmPassword: "Confirm Password required",
        });
      } else if (password === "") {
        setPasswordErr("New Password required");
        resolve({
          password: "New Password required",
          confirmPassword: "",
        });
      } else if (!validatePassword(password)) {
        setPasswordErr("The entered password doesn't meet the required password policy listed below");
        resolve({
          password: "The entered password doesn't meet the required password policy listed below",
          confirmPassword: "",
        });
      } else if (confirmPassword === "") {
        setConfirmPasswordErr("Confirm Password required");
        resolve({
          password: "",
          confirmPassword: "Confirm Password required",
        });
      } else if (!validatePassword(confirmPassword)) {
        setConfirmPasswordErr("Entered Confirm Password in not matching");
        resolve({
          password: "",
          confirmPassword: "Entered Confirm Password in not matching",
        });
      } else if (confirmPassword !== password) {
        setConfirmPasswordErr("Confirm Password and New Password must be same");
        resolve({
          password: "",
          confirmPassword: "Confirm Password and New Password must be same",
        });
      } else {
        resolve({ password: "", confirmPassword: "" });
      }
      reject("");
    });
  };

  const validatePassword = (password) => {
     const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.\[\]{}()?"!\-@#%&\/\\,><':;|_~`+=])[A-Za-z\d\^$*.\[\]{}()?"!\-@#%&\/\\,><':;|_~`+=]{8,}$/;
    return regex.test(password);
  };

  // Keyed on the Cognito error code, as ResetPassword does.
  const errorMessages = {
    InvalidPasswordException: "The password does not meet the password policy.",
    // Mostly the three-minute challenge session expiring (Cognito also uses it for a disabled
    // user); a retry cannot succeed, only a fresh sign-in.
    NotAuthorizedException:
      "Your session has expired or this account cannot sign in. Go back to the login page and sign in again with your temporary password.",
    InvalidParameterException: "Your account is missing required details. Please contact support.",
    LimitExceededException: "Attempt limit exceeded, please try after some time.",
  };

  const handleChangePassword = () => {
    setIsFormSubmitted(true);
    setPwdChangeErr("");
    setPasswordErr("");
    setConfirmPasswordErr("");
    validation()
      .then(
        (res) => {
          if (res.password === "" && res.confirmPassword === "") {
            completeNewPasswordChallenge(password)
              .then(
                async () => {
                  // The SDK cached the new session; load it while the challenge is still
                  // pending (so this page stays put), then tell SignIn this is a finished login
                  // so it runs the terms check and dealer onboarding instead of showing the form.
                  await getCurrentUser();
                  completedRef.current = true;
                  resetNewPasswordAttr();
                  restForm();
                  navigate("/", { replace: true, state: { loginTriggered: true } });
                },
                (err) => {
                  console.log("Password change service issue :", err);
                  setPwdChangeErr(errorMessages[err?.code] || err?.message || "An error occurred.");
                  setIsFormSubmitted(false);
                }
              )
              .catch((err) => {
                console.error("Error", err);
                setPwdChangeErr("Internal Server error");
                setIsFormSubmitted(false);
              });
          } else {
            setIsFormSubmitted(false);
          }
        },
        (err) => {
          console.log("Error validating the password :", err);
          setIsFormSubmitted(false);
        }
      )
      .catch((err) => {
        console.log(err);
        setIsFormSubmitted(false);
      });
  };

  const restForm = () => {
    setPasswordErr("");
    setConfirmPasswordErr("");
    setPassword("");
    setConfirmPassword("");
    setIsFormSubmitted(false);
  };

  return (
    <ThemeProvider theme={appTheme}>
      <Grid container sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: `url(${loginbg})`,
            backgroundRepeat: "no-repeat",
            backgroundColor: (t) =>
              t.palette.mode === "light"
                ? t.palette.grey[50]
                : t.palette.grey[900],
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box
            m="auto"
            sx={{
              width: 300,
              my: 8,
              mx: 4,
              ml: 20,
              mt: 20,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography component="h1" variant="h4">
              Hello Again!
            </Typography>
            <Typography component="h1" variant="h6">
              Please Change Your Password
            </Typography>
            <Box component="form" noValidate>
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="stretch"
              >
                <Grid item xs={12} sm={6} md={6}>
                  <IconTextField
                    iconStart={
                      <LockOutlinedIcon
                        sx={{ color: "#bdbdbd", fontSize: 20 }}
                      />
                    }
                    iconEnd={
                      <IconButton
                        aria-label={
                          showPassword
                            ? "hide the password"
                            : "display the password"
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    }
                    margin="normal"
                    id="newPassword"
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    icon={<LockOutlinedIcon />}
                    fullWidth
                    value={password}
                    onChange={(e) =>
                      formInputChange("newPassword", e.target.value)
                    }
                    error={passwordErr ? true : false}
                    helperText={passwordErr}
                    size="small"
                    disabled={isFormSubmitted ? true : false}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <IconTextField
                    iconStart={
                      <LockOutlinedIcon
                        sx={{ color: "#bdbdbd", fontSize: 20 }}
                      />
                    }
                    margin="normal"
                    id="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    value={confirmPassword}
                    onChange={(e) =>
                      formInputChange("confirmPassword", e.target.value)
                    }
                    error={confirmPasswordErr ? true : false}
                    helperText={confirmPasswordErr}
                    size="small"
                    disabled={isFormSubmitted ? true : false}
                  />
                </Grid>
                {confirmPassword.length > 0 && (
                  <Grid item xs={12} sm={6} md={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      {password === confirmPassword ? (
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#4caf50" }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: 14, color: "#f44336" }} />
                      )}
                      <Typography
                        variant="body"
                        sx={{ fontSize: "12px", color: password === confirmPassword ? "#4caf50" : "#f44336" }}
                      >
                        {password === confirmPassword ? "Passwords match" : "Passwords do not match"}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={6}>
                  <Typography
                    variant="body"
                    sx={{ color: "#f44336", fontSize: "10px" }}
                  >
                    {pwdChangeErr}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <Stack spacing={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 3,
                        mb: 2,
                        borderRadius: "50px",
                      }}
                      size="small"
                      onClick={handleChangePassword}
                      disabled={isFormSubmitted ? true : false}
                    >
                      Change Password
                    </Button>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={homeNavigation}
                      size="small"
                      startIcon={<ArrowBackIcon />}
                    >
                      Back to Login Page
                    </Button>
                  </Stack>
                </Grid>
                {password.length > 0 && (
                  <Grid item xs={12} sm={6} md={6}>
                    {(() => {
                      const rules = [
                        { label: "Minimum 8 characters", pass: password.length >= 8 },
                        { label: "At least 1 number", pass: /\d/.test(password) },
                        { label: "At least 1 special character (@$!%*?&)", pass: /[@$!%*?&]/.test(password) },
                        { label: "At least 1 uppercase letter", pass: /[A-Z]/.test(password) },
                        { label: "At least 1 lowercase letter", pass: /[a-z]/.test(password) },
                      ];
                      const passCount = rules.filter((r) => r.pass).length;
                      const strengthMap = [
                        { label: "Weak", color: "#f44336", value: 20 },
                        { label: "Weak", color: "#f44336", value: 20 },
                        { label: "Fair", color: "#ff9800", value: 40 },
                        { label: "Fair", color: "#ff9800", value: 60 },
                        { label: "Good", color: "#FFB200", value: 80 },
                        { label: "Strong", color: "#4caf50", value: 100 },
                      ];
                      const strength = strengthMap[passCount];
                      return (
                        <>
                          <Box sx={{ mt: 1, mb: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="body" sx={{ fontSize: "11px", color: "#888" }}>
                                Password strength
                              </Typography>
                              <Typography variant="body" sx={{ fontSize: "11px", fontWeight: "bold", color: strength.color }}>
                                {strength.label}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={strength.value}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: "#e0e0e0",
                                "& .MuiLinearProgress-bar": { backgroundColor: strength.color },
                              }}
                            />
                          </Box>
                          <List dense disablePadding>
                            {rules.map((rule) => (
                              <ListItem key={rule.label} disableGutters sx={{ py: 0 }}>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      {rule.pass ? (
                                        <CheckCircleIcon sx={{ fontSize: 14, color: "#4caf50" }} />
                                      ) : (
                                        <CancelIcon sx={{ fontSize: 14, color: "#f44336" }} />
                                      )}
                                      <Typography
                                        variant="body"
                                        sx={{ fontSize: "12px", color: rule.pass ? "#4caf50" : "#f44336" }}
                                      >
                                        {rule.label}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      );
                    })()}
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default ChangePassword;
